import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { 
  FileSpreadsheet, Download, Loader2, Calendar, Users, JapaneseYen,
  FileText, Table2, User
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const WageLedgerPage = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState("monthly");
  
  // 年月選択
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  
  // 従業員選択（従業員別台帳用）
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  
  // 月別データ
  const [monthlyData, setMonthlyData] = useState(null);
  
  // 年間データ
  const [yearlyData, setYearlyData] = useState(null);
  
  // 従業員別データ
  const [employeeData, setEmployeeData] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeTab === "monthly") {
      fetchMonthlyData();
    } else if (activeTab === "yearly") {
      fetchYearlyData();
    } else if (activeTab === "employee" && selectedEmployee) {
      fetchEmployeeData();
    }
  }, [activeTab, selectedYear, selectedMonth, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data || []);
      if (res.data?.length > 0) {
        setSelectedEmployee(res.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wage-ledger/monthly?year=${selectedYear}&month=${selectedMonth}&format=json`);
      setMonthlyData(res.data);
    } catch (error) {
      console.error("Error fetching monthly data:", error);
      setMonthlyData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearlyData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wage-ledger/yearly?year=${selectedYear}&format=json`);
      setYearlyData(res.data);
    } catch (error) {
      console.error("Error fetching yearly data:", error);
      setYearlyData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeData = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wage-ledger/employee/${selectedEmployee}?year=${selectedYear}&format=json`);
      setEmployeeData(res.data);
    } catch (error) {
      console.error("Error fetching employee data:", error);
      setEmployeeData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (type, format) => {
    const key = `${type}-${format}`;
    setDownloading(key);
    try {
      let url = "";
      let filename = "";
      
      if (type === "monthly") {
        url = `${API}/wage-ledger/monthly?year=${selectedYear}&month=${selectedMonth}&format=${format}`;
        filename = `賃金台帳_${selectedYear}年${selectedMonth}月.${format}`;
      } else if (type === "yearly") {
        url = `${API}/wage-ledger/yearly?year=${selectedYear}&format=${format}`;
        filename = `賃金台帳_${selectedYear}年.${format}`;
      } else if (type === "employee") {
        url = `${API}/wage-ledger/employee/${selectedEmployee}?year=${selectedYear}&format=${format}`;
        const emp = employees.find(e => e.id === selectedEmployee);
        filename = `賃金台帳_${emp?.name || selectedEmployee}_${selectedYear}年.${format}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${filename} をダウンロードしました`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("ダウンロードに失敗しました");
    } finally {
      setDownloading(null);
    }
  };

  const formatCurrency = (amount) => {
    return `¥${(amount || 0).toLocaleString()}`;
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-emerald-500" />
            賃金台帳
          </h1>
          <p className="text-slate-400 text-sm mt-1">月別・年間・従業員別の賃金台帳をCSV/PDFで出力</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border border-slate-700 flex flex-wrap h-auto p-1">
          <TabsTrigger value="monthly" className="data-[state=active]:bg-emerald-600 gap-2" data-testid="tab-monthly">
            <Calendar className="w-4 h-4" />
            月別台帳
          </TabsTrigger>
          <TabsTrigger value="yearly" className="data-[state=active]:bg-emerald-600 gap-2" data-testid="tab-yearly">
            <Table2 className="w-4 h-4" />
            年間台帳
          </TabsTrigger>
          <TabsTrigger value="employee" className="data-[state=active]:bg-emerald-600 gap-2" data-testid="tab-employee">
            <User className="w-4 h-4" />
            従業員別台帳
          </TabsTrigger>
        </TabsList>

        {/* 月別台帳タブ */}
        <TabsContent value="monthly" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-white text-lg">月別賃金台帳</CardTitle>
                  <CardDescription>指定月の全従業員の賃金データを出力</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-28 bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const year = currentDate.getFullYear() - 2 + i;
                        return <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-24 bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}月</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : monthlyData?.data?.length > 0 ? (
                <>
                  {/* サマリー */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <Users className="w-4 h-4" />
                        対象者数
                      </div>
                      <p className="text-2xl font-bold text-white">{monthlyData.total_employees}名</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <JapaneseYen className="w-4 h-4" />
                        総支給額合計
                      </div>
                      <p className="text-2xl font-bold text-emerald-400">
                        {formatCurrency(monthlyData.data.reduce((sum, d) => sum + d.gross_salary, 0))}
                      </p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <JapaneseYen className="w-4 h-4" />
                        控除額合計
                      </div>
                      <p className="text-2xl font-bold text-red-400">
                        {formatCurrency(monthlyData.data.reduce((sum, d) => sum + d.total_deductions, 0))}
                      </p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <JapaneseYen className="w-4 h-4" />
                        差引支給額合計
                      </div>
                      <p className="text-2xl font-bold text-amber-400">
                        {formatCurrency(monthlyData.data.reduce((sum, d) => sum + d.net_salary, 0))}
                      </p>
                    </div>
                  </div>

                  {/* テーブル */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 text-slate-400 font-medium">社員</th>
                          <th className="text-right p-3 text-slate-400 font-medium">出勤日数</th>
                          <th className="text-right p-3 text-slate-400 font-medium">残業h</th>
                          <th className="text-right p-3 text-slate-400 font-medium">基本給</th>
                          <th className="text-right p-3 text-slate-400 font-medium">総支給額</th>
                          <th className="text-right p-3 text-slate-400 font-medium">社保計</th>
                          <th className="text-right p-3 text-slate-400 font-medium">所得税</th>
                          <th className="text-right p-3 text-slate-400 font-medium">差引支給額</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.data.map((item, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-3">
                              <div className="text-white font-medium">{item.employee_name}</div>
                              <div className="text-slate-400 text-xs">{item.employee_code}</div>
                            </td>
                            <td className="p-3 text-right text-white">{item.working_days}日</td>
                            <td className="p-3 text-right text-amber-400">{item.overtime_hours.toFixed(1)}h</td>
                            <td className="p-3 text-right text-white">{formatCurrency(item.base_salary)}</td>
                            <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(item.gross_salary)}</td>
                            <td className="p-3 text-right text-slate-300">
                              {formatCurrency(item.health_insurance + item.nursing_insurance + item.pension + item.employment_insurance)}
                            </td>
                            <td className="p-3 text-right text-slate-300">{formatCurrency(item.income_tax)}</td>
                            <td className="p-3 text-right text-white font-bold">{formatCurrency(item.net_salary)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>給与データがありません</p>
                  <p className="text-sm mt-1">給与計算を実行してからご利用ください</p>
                </div>
              )}

              {/* ダウンロードボタン */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-700">
                <Button
                  onClick={() => downloadFile('monthly', 'csv')}
                  disabled={downloading !== null || !monthlyData?.data?.length}
                  variant="outline"
                  className="border-slate-600 hover:bg-slate-700"
                  data-testid="btn-monthly-csv"
                >
                  {downloading === 'monthly-csv' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  CSV出力
                </Button>
                <Button
                  onClick={() => downloadFile('monthly', 'pdf')}
                  disabled={downloading !== null || !monthlyData?.data?.length}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-monthly-pdf"
                >
                  {downloading === 'monthly-pdf' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  PDF出力
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 年間台帳タブ */}
        <TabsContent value="yearly" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-white text-lg">年間賃金台帳</CardTitle>
                  <CardDescription>指定年の全従業員の年間累計データを出力</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-28 bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const year = currentDate.getFullYear() - 2 + i;
                        return <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : yearlyData?.data?.length > 0 ? (
                <>
                  {/* サマリー */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <Users className="w-4 h-4" />
                        対象者数
                      </div>
                      <p className="text-2xl font-bold text-white">{yearlyData.total_employees}名</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <JapaneseYen className="w-4 h-4" />
                        年間総支給額
                      </div>
                      <p className="text-2xl font-bold text-emerald-400">
                        {formatCurrency(yearlyData.data.reduce((sum, d) => sum + d.total_gross_salary, 0))}
                      </p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <JapaneseYen className="w-4 h-4" />
                        年間控除額
                      </div>
                      <p className="text-2xl font-bold text-red-400">
                        {formatCurrency(yearlyData.data.reduce((sum, d) => sum + d.total_deductions, 0))}
                      </p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                        <JapaneseYen className="w-4 h-4" />
                        年間差引支給額
                      </div>
                      <p className="text-2xl font-bold text-amber-400">
                        {formatCurrency(yearlyData.data.reduce((sum, d) => sum + d.total_net_salary, 0))}
                      </p>
                    </div>
                  </div>

                  {/* テーブル */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 text-slate-400 font-medium">社員</th>
                          <th className="text-right p-3 text-slate-400 font-medium">勤務月数</th>
                          <th className="text-right p-3 text-slate-400 font-medium">出勤日数</th>
                          <th className="text-right p-3 text-slate-400 font-medium">残業h</th>
                          <th className="text-right p-3 text-slate-400 font-medium">年間総支給</th>
                          <th className="text-right p-3 text-slate-400 font-medium">年間社保</th>
                          <th className="text-right p-3 text-slate-400 font-medium">年間所得税</th>
                          <th className="text-right p-3 text-slate-400 font-medium">年間手取</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearlyData.data.map((item, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-3">
                              <div className="text-white font-medium">{item.employee_name}</div>
                              <div className="text-slate-400 text-xs">{item.employee_code}</div>
                            </td>
                            <td className="p-3 text-right text-white">{item.months_worked}ヶ月</td>
                            <td className="p-3 text-right text-white">{item.total_working_days}日</td>
                            <td className="p-3 text-right text-amber-400">{item.total_overtime_hours.toFixed(1)}h</td>
                            <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(item.total_gross_salary)}</td>
                            <td className="p-3 text-right text-slate-300">
                              {formatCurrency(item.total_health_insurance + item.total_nursing_insurance + item.total_pension + item.total_employment_insurance)}
                            </td>
                            <td className="p-3 text-right text-slate-300">{formatCurrency(item.total_income_tax)}</td>
                            <td className="p-3 text-right text-white font-bold">{formatCurrency(item.total_net_salary)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>給与データがありません</p>
                  <p className="text-sm mt-1">給与計算を実行してからご利用ください</p>
                </div>
              )}

              {/* ダウンロードボタン */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-700">
                <Button
                  onClick={() => downloadFile('yearly', 'csv')}
                  disabled={downloading !== null || !yearlyData?.data?.length}
                  variant="outline"
                  className="border-slate-600 hover:bg-slate-700"
                  data-testid="btn-yearly-csv"
                >
                  {downloading === 'yearly-csv' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  CSV出力
                </Button>
                <Button
                  onClick={() => downloadFile('yearly', 'pdf')}
                  disabled={downloading !== null || !yearlyData?.data?.length}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-yearly-pdf"
                >
                  {downloading === 'yearly-pdf' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  PDF出力
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 従業員別台帳タブ */}
        <TabsContent value="employee" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-white text-lg">従業員別年間台帳</CardTitle>
                  <CardDescription>選択した従業員の年間賃金データを月別に出力</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="w-48 bg-slate-900 border-slate-700">
                      <SelectValue placeholder="従業員を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.employee_code}: {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-28 bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const year = currentDate.getFullYear() - 2 + i;
                        return <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : employeeData?.monthly_data?.length > 0 ? (
                <>
                  {/* 従業員情報 */}
                  <div className="bg-slate-900 p-4 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {employeeData.employee.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{employeeData.employee.name}</p>
                        <p className="text-slate-400 text-sm">
                          {employeeData.employee.code} | {employeeData.employee.department} | {employeeData.employee.position}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 年間サマリー */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="text-slate-400 text-sm mb-1">年間出勤日数</div>
                      <p className="text-2xl font-bold text-white">{employeeData.yearly_totals.total_working_days}日</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="text-slate-400 text-sm mb-1">年間総支給額</div>
                      <p className="text-2xl font-bold text-emerald-400">{formatCurrency(employeeData.yearly_totals.total_gross_salary)}</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="text-slate-400 text-sm mb-1">年間控除額</div>
                      <p className="text-2xl font-bold text-red-400">{formatCurrency(employeeData.yearly_totals.total_deductions)}</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <div className="text-slate-400 text-sm mb-1">年間差引支給額</div>
                      <p className="text-2xl font-bold text-amber-400">{formatCurrency(employeeData.yearly_totals.total_net_salary)}</p>
                    </div>
                  </div>

                  {/* 月別テーブル */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 text-slate-400 font-medium">月</th>
                          <th className="text-right p-3 text-slate-400 font-medium">出勤</th>
                          <th className="text-right p-3 text-slate-400 font-medium">残業h</th>
                          <th className="text-right p-3 text-slate-400 font-medium">基本給</th>
                          <th className="text-right p-3 text-slate-400 font-medium">総支給</th>
                          <th className="text-right p-3 text-slate-400 font-medium">社保計</th>
                          <th className="text-right p-3 text-slate-400 font-medium">所得税</th>
                          <th className="text-right p-3 text-slate-400 font-medium">差引支給</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeData.monthly_data.map((item, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-3 text-white font-medium">{item.month}月</td>
                            <td className="p-3 text-right text-white">{item.working_days}日</td>
                            <td className="p-3 text-right text-amber-400">{item.overtime_hours.toFixed(1)}h</td>
                            <td className="p-3 text-right text-white">{formatCurrency(item.base_salary)}</td>
                            <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(item.gross_salary)}</td>
                            <td className="p-3 text-right text-slate-300">
                              {formatCurrency(item.health_insurance + item.nursing_insurance + item.pension + item.employment_insurance)}
                            </td>
                            <td className="p-3 text-right text-slate-300">{formatCurrency(item.income_tax)}</td>
                            <td className="p-3 text-right text-white font-bold">{formatCurrency(item.net_salary)}</td>
                          </tr>
                        ))}
                        {/* 年間合計行 */}
                        <tr className="bg-slate-700/50 font-bold">
                          <td className="p-3 text-emerald-400">年間合計</td>
                          <td className="p-3 text-right text-white">{employeeData.yearly_totals.total_working_days}日</td>
                          <td className="p-3 text-right text-amber-400">{employeeData.yearly_totals.total_overtime_hours.toFixed(1)}h</td>
                          <td className="p-3 text-right text-white">-</td>
                          <td className="p-3 text-right text-emerald-400">{formatCurrency(employeeData.yearly_totals.total_gross_salary)}</td>
                          <td className="p-3 text-right text-slate-300">
                            {formatCurrency(
                              employeeData.yearly_totals.total_health_insurance +
                              employeeData.yearly_totals.total_nursing_insurance +
                              employeeData.yearly_totals.total_pension +
                              employeeData.yearly_totals.total_employment_insurance
                            )}
                          </td>
                          <td className="p-3 text-right text-slate-300">{formatCurrency(employeeData.yearly_totals.total_income_tax)}</td>
                          <td className="p-3 text-right text-white">{formatCurrency(employeeData.yearly_totals.total_net_salary)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>給与データがありません</p>
                  <p className="text-sm mt-1">従業員を選択し、給与計算を実行してからご利用ください</p>
                </div>
              )}

              {/* ダウンロードボタン */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-700">
                <Button
                  onClick={() => downloadFile('employee', 'csv')}
                  disabled={downloading !== null || !employeeData?.monthly_data?.length}
                  variant="outline"
                  className="border-slate-600 hover:bg-slate-700"
                  data-testid="btn-employee-csv"
                >
                  {downloading === 'employee-csv' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  CSV出力
                </Button>
                <Button
                  onClick={() => downloadFile('employee', 'pdf')}
                  disabled={downloading !== null || !employeeData?.monthly_data?.length}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-employee-pdf"
                >
                  {downloading === 'employee-pdf' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  PDF出力
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WageLedgerPage;
